import { SKY_API_ENDPOINT } from './apiFetcher';
import type { ResponseError } from './apiFetcher';
import useApi from './hooks/useApi';

// The Financials page reads sky-api (/internal/atlas/financials/) and composes
// its figures the way skyeco's Revenue page does (financial.skyeco.com).

export const NET_REVENUE_INFO =
  'Revenue less all expenses, including Security and Maintenance. This is a different figure from the Net Revenue of the Monthly Settlement Cycle (MSC). The MSC states Net Revenue before Security and Maintenance. This line also counts each payment on the date it arrives. The MSC counts what was paid for its month, even when the payment arrives in the next month. PSM and Prime payments often arrive late.';

// skyeco restates this revenue_distribution category as an expense.
const SECURITY_AND_MAINTENANCE = 'Security and Maintenance';

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const DAY_MS = 24 * 60 * 60 * 1000;

export type SettlementRow = {
  slug: string;
  name: string;
  period: string;
  reporting_start_date: string;
  reporting_end_date: string;
  income: number;
  expenses: number;
  net_revenue: number | null;
};

export type Projection = {
  income: number;
  expenses: number;
  net_revenue: number;
  months_covered: number;
  basis: SettlementRow[];
};

export type Ytd = {
  year: number;
  gross_revenue: number;
  expenses: number;
  net_revenue: number;
};

function periodLabel(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const year = String(e.getUTCFullYear()).slice(2);
  if (
    s.getUTCFullYear() === e.getUTCFullYear() &&
    s.getUTCMonth() === e.getUTCMonth()
  ) {
    return `${SHORT_MONTHS[s.getUTCMonth()]} ${year}`;
  }
  return `${SHORT_MONTHS[s.getUTCMonth()]}–${SHORT_MONTHS[e.getUTCMonth()]} ${year}`;
}

function monthsBetween(start: string, end: string): number {
  const days =
    (new Date(end).getTime() - new Date(start).getTime()) / DAY_MS + 1;
  return Math.max(1, Math.round(days / 30));
}

function amount(value: string | number | null | undefined): number | null {
  return value === null || value === undefined || value === ''
    ? null
    : Number(value);
}

// Every executed cycle with a reporting period, oldest first.
export function useSettlements(): [
  SettlementRow[] | undefined,
  ResponseError | undefined
] {
  const [response, error] = useApi({
    baseUrl: SKY_API_ENDPOINT,
    url: '/financials/settlements/'
  });
  const rows = response?.data
    ?.filter((m: any) => m.reporting_start_date && m.reporting_end_date)
    .map((m: any) => ({
      slug: m.slug,
      name: m.name,
      period: periodLabel(m.reporting_start_date, m.reporting_end_date),
      reporting_start_date: m.reporting_start_date,
      reporting_end_date: m.reporting_end_date,
      income: amount(m.income) ?? 0,
      expenses: amount(m.expenses) ?? 0,
      net_revenue: amount(m.net_revenue)
    }));
  return [rows, error];
}

// The last 3 cycles with a governance-reported net revenue, scaled to 12 months.
export function buildProjection(rows: SettlementRow[]): Projection | null {
  const basis = rows.filter((row) => row.net_revenue !== null).slice(-3);
  if (basis.length === 0) {
    return null;
  }
  const months = basis.reduce(
    (sum, row) =>
      sum + monthsBetween(row.reporting_start_date, row.reporting_end_date),
    0
  );
  const factor = 12 / months;
  const total = (field: 'income' | 'expenses' | 'net_revenue') =>
    basis.reduce((sum, row) => sum + (row[field] ?? 0), 0) * factor;
  return {
    income: total('income'),
    expenses: total('expenses'),
    net_revenue: total('net_revenue'),
    months_covered: months,
    basis
  };
}

// The projection as it stood at each of the last `limit` cycles.
export function buildProjectionSeries(
  rows: SettlementRow[],
  limit: number
): (Projection & { period: string })[] {
  return rows.slice(-limit).flatMap((row) => {
    const projection = buildProjection(
      rows.filter((r) => r.reporting_end_date <= row.reporting_end_date)
    );
    return projection ? [{ ...projection, period: row.period }] : [];
  });
}

export function useYtd(): [Ytd | undefined, ResponseError | undefined] {
  const year = new Date().getUTCFullYear();
  const [response, error] = useApi({
    baseUrl: SKY_API_ENDPOINT,
    url: '/financials/profit-and-loss/',
    queryParams: { year }
  });
  const statement = response?.data;
  if (!statement) {
    return [undefined, error];
  }
  const securityAndMaintenance =
    statement.groups
      .find((group: any) => group.type === 'revenue_distribution')
      ?.categories.find(
        (category: any) => category.category === SECURITY_AND_MAINTENANCE
      )?.amount ?? 0;
  const grossRevenue = Number(statement.totals.revenue ?? 0);
  const expenses =
    Number(statement.totals.expense ?? 0) + Number(securityAndMaintenance);
  return [
    {
      year,
      gross_revenue: grossRevenue,
      expenses,
      net_revenue: grossRevenue - expenses
    },
    error
  ];
}
